import { cn } from '@ps/ui/utils';

import { isBodyTruncatable } from '../../../functions/previewText';
import { ExpandableTextBody } from './ExpandableTextBody';

interface Props {
  /** Sanitized rich-text HTML from the creator's editor. */
  bodyHtml?: string;
  /** Permalink the "Read more" link points to (full body lives there). */
  permalinkHref?: string;
  /**
   * Open the post in place instead of navigating to the permalink. Supplied by
   * the creator wall, whose permalink `<a>` would hard-nav out of the dashboard
   * into the bio route group and 404. When set (feed context), "Read more…"
   * becomes a `<button>`; omitted on the visitor feed → it stays the `<a>` link.
   */
  onOpen?: () => void;
  /**
   * Detail context (post-detail modal / permalink page) — the FULL body lives
   * here, so "Read more…" toggles expand/collapse IN PLACE (Read more ⇄ Read
   * less) via {@link ExpandableTextBody} instead of navigating (a nav would loop
   * back onto the same view). The feed leaves this off and links out to open the
   * post instead.
   */
  detail?: boolean;
}

/**
 * Rich-text post body — the sanitized HTML, clipped to a max height with a
 * "Read more" CTA when long. Whether to clip is decided HERE at render from the
 * body's length (`isBodyTruncatable`), not carried on the view model.
 *
 * Two modes:
 *  - feed (default): server-rendered; "Read more…" opens the post (a `<button>`
 *    when `onOpen` is set, else a permalink `<a>`).
 *  - `detail`: the full body is on this surface, so it delegates to the client
 *    {@link ExpandableTextBody} for an in-place Read more ⇄ Read less toggle.
 */
export function PostTextBody({
  bodyHtml,
  permalinkHref,
  onOpen,
  detail = false,
}: Props) {
  if (!bodyHtml) {
    return null;
  }
  if (detail) {
    // The detail view measures real overflow itself (no char-count heuristic).
    return <ExpandableTextBody bodyHtml={bodyHtml} />;
  }

  const truncatable = isBodyTruncatable(bodyHtml);
  const readMoreClass =
    'text-brand-text mt-1 inline-block text-[14px] font-semibold';
  return (
    <div>
      <div
        className={cn(
          'post-html-body text-[14px] leading-relaxed opacity-90',
          truncatable && 'max-h-72 overflow-hidden',
        )}
        dangerouslySetInnerHTML={{ __html: bodyHtml }}
      />
      {truncatable ? (
        onOpen ? (
          <button type="button" onClick={onOpen} className={readMoreClass}>
            Read more…
          </button>
        ) : permalinkHref ? (
          <a href={permalinkHref} className={readMoreClass}>
            Read more…
          </a>
        ) : null
      ) : null}
    </div>
  );
}
